import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Menu, X, ShoppingCart, ArrowRight } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { useCart } from './CartContext';
import { motion, AnimatePresence } from 'framer-motion';
import { categories } from '../data/products';
import { ChevronDown } from 'lucide-react';
interface NavbarProps {
  isDark: boolean;
  toggleDark: () => void;
}
export function Navbar({
  isDark,
  toggleDark
}: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const [mobileHelpOpen, setMobileHelpOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [logoRipple, setLogoRipple] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [navRetracted, setNavRetracted] = useState(false);
  const lastScrollY = useRef(0);
  // Reset mobile dropdowns when menu closes
  useEffect(() => {
    if (!isOpen) {
      setMobileShopOpen(false);
      setMobileHelpOpen(false);
      setMobileAboutOpen(false);
    }
  }, [isOpen]);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 50);
      // Mobile retract: slide up 50% when scrolling down, restore at top
      const isMobile = window.innerWidth < 1300;
      if (!isMobile) {
        setNavRetracted(false);
      } else if (y === 0) {
        setNavRetracted(false);
      } else if (y > lastScrollY.current && y > 60) {
        setNavRetracted(true);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    totalItems,
    toggleCart
  } = useCart();
  // Helper to handle anchor links from other pages
  const getAnchorHref = (hash: string) => {
    if (location.pathname !== '/') {
      return `/${hash}`;
    }
    return hash;
  };
  const goToQuote = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      navigate('/');
    }
  }, [location.pathname, navigate]);
  const collapseCard = useCallback(() => {
    window.dispatchEvent(new Event('materialsCollapse'));
  }, []);
  const resetMaterials = useCallback(() => {
    collapseCard();
    // Also reset active tab to materials
    window.dispatchEvent(new Event('materialsReset'));
  }, [collapseCard]);
  const goToProduct = useCallback((categoryName: string, productIndex: number) => {
    const params = new URLSearchParams({
      cat: categoryName,
      prod: String(productIndex)
    });
    if (location.pathname !== '/') {
      navigate(`/?${params.toString()}#materials`);
    } else {
      window.history.replaceState(null, '', `/?${params.toString()}#materials`);
      document.getElementById('materials')?.scrollIntoView({
        behavior: 'smooth'
      });
      // Dispatch event so Services picks up the change
      window.dispatchEvent(new Event('materialsNav'));
    }
  }, [location.pathname, navigate]);
  return <nav className={`sticky top-0 z-50 w-full transition-all duration-300 border-b ${isScrolled ? 'bg-white dark:bg-[#070707] backdrop-blur-md shadow-lg border-stone-200 dark:border-white/20' : 'bg-white/60 dark:bg-[#070707]/50 border-stone-200/50 dark:border-white/20'}`} style={{
    overflow: navRetracted && !isOpen ? 'hidden' : 'visible',
    height: navRetracted && !isOpen ? '56px' : '113px',
    transition: 'height 0.3s ease'
  }} data-id="element-548">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-549">
        <div className="flex justify-between items-center" style={{
        height: navRetracted && !isOpen ? '56px' : '113px',
        transition: 'height 0.3s ease'
      }} data-id="element-550">
          <div className="flex-shrink-0 flex items-center" data-id="element-551">
            <Link to="/" onClick={resetMaterials} className="relative" data-id="element-552">
              <motion.img src="https://cdn.magicpatterns.com/uploads/pv38EKqBFfU3b2ryMtdaxz/NEW_GRAVEL_LOGO_bw.png" alt="253Gravel" className="h-11 max-sm:h-10 w-auto" style={{
              filter: 'brightness(0) invert(1)'
            }} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 2,
              ease: 'easeOut'
            }} data-id="element-553" />
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8 font-nav" data-id="element-554">
            {/* Shop Dropdown */}
            <div className="relative" onMouseEnter={() => setIsShopOpen(true)} onMouseLeave={() => setIsShopOpen(false)} data-id="element-555">
              <button className="flex items-center text-stone-800 dark:text-white hover:text-brand-orange font-medium transition-colors py-2" data-id="element-556">
                Shop <ChevronDown className="w-4 h-4 ml-1" data-id="element-557" />
              </button>

              <AnimatePresence data-id="element-558">
                {isShopOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 bg-white/95 dark:bg-[#070707]/85 backdrop-blur-md border border-stone-200 dark:border-white/20 rounded-xl shadow-xl p-5 z-50" style={{
                width: '620px'
              }} data-id="element-559">
                    <div className="grid grid-cols-3 gap-5" data-id="element-560">
                      {categories.map(cat => <div key={cat.name} data-id="element-561">
                          <p className="text-xs font-bold text-brand-muted uppercase tracking-wider mb-2" data-id="element-562">
                            {cat.name}
                          </p>
                          <div className="space-y-1.5" data-id="element-563">
                            {cat.products.map((prod, prodIdx) => <button key={prod.slug} onClick={() => {
                        setIsShopOpen(false);
                        goToProduct(cat.name, prodIdx);
                      }} className="block text-sm text-stone-600 dark:text-stone-300 hover:text-brand-orange transition-colors text-left w-full" data-id="element-564">
                                {prod.name}
                              </button>)}
                          </div>
                        </div>)}
                    </div>

                    <div className="mt-6 pt-4 border-t border-stone-200 dark:border-white/10" data-id="element-565">
                      <Link to="/subcontractors" onClick={() => {
                    setIsShopOpen(false);
                    collapseCard();
                  }} className="flex items-center justify-between bg-brand-orange/10 hover:bg-brand-orange/20 border border-brand-orange/30 rounded-lg p-3 transition-colors group" data-id="element-566">
                        <div data-id="element-567">
                          <span className="block text-brand-orange font-bold text-sm" data-id="element-568">
                            Contractor Ordering
                          </span>
                          <span className="block text-stone-600 dark:text-stone-200 text-xs mt-0.5" data-id="element-569">
                            Bulk pricing & volume discounts
                          </span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-brand-orange opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" data-id="element-570" />
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <button onClick={() => {
            collapseCard();
            setIsOpen(false);
            if (location.pathname !== '/') {
              navigate('/');
              setTimeout(() => {
                document.getElementById('materials')?.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new Event('materialsNav'));
              }, 300);
            } else {
              document.getElementById('materials')?.scrollIntoView({
                behavior: 'smooth'
              });
              window.dispatchEvent(new Event('materialsNav'));
            }
          }} className="text-stone-800 dark:text-white hover:text-brand-orange font-medium transition-colors" data-id="element-571">
              Materials
            </button>
            <div className="relative" onMouseEnter={() => setIsHelpOpen(true)} onMouseLeave={() => setIsHelpOpen(false)} data-id="element-572">
              <button className="flex items-center text-stone-800 dark:text-white hover:text-brand-orange font-medium transition-colors py-2" data-id="element-573">
                Help Me <ChevronDown className="w-4 h-4 ml-1" data-id="element-574" />
              </button>
              <AnimatePresence data-id="element-575">
                {isHelpOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 bg-white/95 dark:bg-[#070707]/85 backdrop-blur-md border border-stone-200 dark:border-white/20 rounded-xl shadow-xl p-4 z-50 w-48" data-id="element-576">
                    <Link to="/help" onClick={() => {
                  setIsHelpOpen(false);
                  collapseCard();
                }} className="block text-sm text-stone-600 dark:text-stone-300 hover:text-brand-orange transition-colors py-1.5" data-id="element-577">
                      Material Quiz
                    </Link>
                    <Link to="/help?tab=calculator" onClick={() => {
                  setIsHelpOpen(false);
                  collapseCard();
                }} className="block text-sm text-stone-600 dark:text-stone-300 hover:text-brand-orange transition-colors py-1.5" data-id="element-578">
                      Calculator
                    </Link>
                    <Link to="/diy-help" onClick={() => {
                  setIsHelpOpen(false);
                  collapseCard();
                }} className="block text-sm text-stone-600 dark:text-stone-300 hover:text-brand-orange transition-colors py-1.5" data-id="element-579">
                      DIY Driveway
                    </Link>
                  </motion.div>}
              </AnimatePresence>
            </div>
            <Link to="/delivery-zones" onClick={collapseCard} className="text-stone-800 dark:text-white hover:text-brand-orange font-medium transition-colors" data-id="element-580">
              Delivery Zones
            </Link>
            <div className="relative" onMouseEnter={() => setIsAboutOpen(true)} onMouseLeave={() => setIsAboutOpen(false)} data-id="element-581">
              <button className="flex items-center text-stone-800 dark:text-white hover:text-brand-orange font-medium transition-colors py-2" data-id="element-582">
                Info <ChevronDown className="w-4 h-4 ml-1" data-id="element-583" />
              </button>
              <AnimatePresence data-id="element-584">
                {isAboutOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 bg-white/95 dark:bg-[#070707]/85 backdrop-blur-md border border-stone-200 dark:border-white/20 rounded-xl shadow-xl p-4 z-50 w-48" data-id="element-585">
                    <Link to="/about" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                }} className="block text-sm text-stone-600 dark:text-stone-300 hover:text-brand-orange transition-colors py-1.5" data-id="element-586">
                      About Us
                    </Link>
                    <Link to="/about#gallery" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                }} className="block text-sm text-stone-600 dark:text-stone-300 hover:text-brand-orange transition-colors py-1.5" data-id="element-587">
                      Project Gallery
                    </Link>
                    <Link to="/subcontractors" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                }} className="block text-sm text-stone-600 dark:text-stone-300 hover:text-brand-orange transition-colors py-1.5" data-id="element-588">
                      Contractors Zone
                    </Link>
                    <Link to="/#faq" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                }} className="block text-sm text-stone-600 dark:text-stone-300 hover:text-brand-orange transition-colors py-1.5" data-id="element-589">
                      Da FAQs
                    </Link>
                  </motion.div>}
              </AnimatePresence>
            </div>
            <Link to="/contact" onClick={collapseCard} className="text-stone-800 dark:text-white hover:text-brand-orange font-medium transition-colors" data-id="element-590">
              Contact
            </Link>

            <div className="flex items-center space-x-4 border-l border-stone-300 dark:border-stone-600 pl-6" data-id="element-591">
              <ThemeToggle isDark={isDark} onToggle={toggleDark} data-id="element-592" />
              {/* Desktop Cart Button */}
              <button onClick={toggleCart} className="relative p-2 text-stone-800 dark:text-white hover:text-brand-orange transition-colors" aria-label="Open cart" data-id="element-593">
                <ShoppingCart className="w-5 h-5" data-id="element-594" />
                <AnimatePresence data-id="element-595">
                  {totalItems > 0 && <motion.span initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} exit={{
                  scale: 0
                }} className="absolute top-0 right-0 -mt-1 -mr-1 flex items-center justify-center w-4 h-4 bg-brand-orange text-white text-[10px] font-bold rounded-full" data-id="element-596">
                      {totalItems}
                    </motion.span>}
                </AnimatePresence>
              </button>

              <a href="tel:206-619-3742" className="flex items-center text-stone-800 dark:text-white hover:text-brand-orange transition-colors font-medium" data-id="element-597">
                <Phone className="w-4 h-4 mr-2 text-brand-muted" data-id="element-598" />
                (206) 619-3742
              </a>
            </div>
          </div>

          <div className="md:hidden flex items-center space-x-4" data-id="element-599">
            <ThemeToggle isDark={isDark} onToggle={toggleDark} data-id="element-600" />
            {/* Mobile Cart Button */}
            <button onClick={toggleCart} className="relative p-2 text-stone-800 dark:text-white hover:text-brand-orange transition-colors" aria-label="Open cart" data-id="element-601">
              <ShoppingCart className="w-5 h-5" data-id="element-602" />
              <AnimatePresence data-id="element-603">
                {totalItems > 0 && <motion.span initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} exit={{
                scale: 0
              }} className="absolute top-0 right-0 flex items-center justify-center w-4 h-4 bg-brand-orange text-white text-[10px] font-bold rounded-full" data-id="element-604">
                    {totalItems}
                  </motion.span>}
              </AnimatePresence>
            </button>

            <button onClick={() => setIsOpen(!isOpen)} className="text-stone-800 dark:text-white hover:text-brand-orange focus:outline-none p-2" data-id="element-605">
              {isOpen ? <X className="h-6 w-6" data-id="element-606" /> : <Menu className="h-6 w-6" data-id="element-607" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-608">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} onClick={() => setIsOpen(false)} className="md:hidden fixed inset-0 bg-black/40 z-[9998]" data-id="element-609" />
            {/* Pull-down menu */}
            <motion.div initial={{
          y: '-100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '-100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="md:hidden fixed top-0 left-0 right-0 bg-white/95 dark:bg-[#070707]/85 backdrop-blur-md shadow-2xl z-[9999] overflow-y-auto max-h-screen" data-id="element-610">
              <div className="flex justify-between items-center p-6" data-id="element-611">
                <img src="https://cdn.magicpatterns.com/uploads/pv38EKqBFfU3b2ryMtdaxz/NEW_GRAVEL_LOGO_bw.png" alt="253Gravel" className="h-10 w-auto" style={{
              filter: isDark ? undefined : 'brightness(0)'
            }} data-id="element-612" />
                <button onClick={() => setIsOpen(false)} className="text-stone-800 dark:text-white hover:text-brand-orange p-2" data-id="element-613">
                  <X className="w-6 h-6" data-id="element-614" />
                </button>
              </div>
              <div className="px-8 pb-10 space-y-7 flex flex-col text-right font-nav" data-id="element-615">
                {/* Mobile Shop Menu - Collapsible */}
                <div className="py-2 border-b border-stone-200 dark:border-[#2a2a2a]" data-id="element-616">
                  <button onClick={() => setMobileShopOpen(!mobileShopOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white font-bold gap-2 text-lg" data-id="element-617">
                    Shop Materials
                    <motion.div animate={{
                  rotate: mobileShopOpen ? 180 : 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-618">
                      <ChevronDown className="w-5 h-5" data-id="element-619" />
                    </motion.div>
                  </button>
                  <AnimatePresence data-id="element-620">
                    {mobileShopOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-621">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-5 pt-4 pr-1" data-id="element-622">
                          {categories.map(cat => <div key={cat.name} data-id="element-623">
                              <p className="text-xs font-bold text-brand-muted uppercase tracking-wider mb-2 text-right" data-id="element-624">
                                {cat.name}
                              </p>
                              <div className="space-y-3" data-id="element-625">
                                {cat.products.map((prod, prodIdx) => <button key={prod.slug} onClick={() => {
                          setIsOpen(false);
                          goToProduct(cat.name, prodIdx);
                        }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right w-full py-1.5" data-id="element-626">
                                    {prod.name}
                                  </button>)}
                              </div>
                            </div>)}
                        </div>

                        <div className="mt-4 pt-3 border-t border-stone-200 dark:border-white/10" data-id="element-627">
                          <Link to="/subcontractors" onClick={() => {
                      setIsOpen(false);
                      collapseCard();
                    }} className="flex items-center justify-between bg-brand-orange/10 hover:bg-brand-orange/20 border border-brand-orange/30 rounded-lg p-3 transition-colors" data-id="element-628">
                            <div className="text-right flex-1" data-id="element-629">
                              <span className="block text-brand-orange font-bold text-sm" data-id="element-630">
                                Contractor Ordering
                              </span>
                              <span className="block text-stone-500 dark:text-stone-300 text-xs mt-0.5" data-id="element-631">
                                Bulk pricing & volume discounts
                              </span>
                            </div>
                            <ArrowRight className="w-4 h-4 text-brand-orange ml-3" data-id="element-632" />
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <div data-id="element-633">
                  <button onClick={() => setMobileHelpOpen(!mobileHelpOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-634">
                    Help Me
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${mobileHelpOpen ? 'rotate-180' : ''}`} data-id="element-635" />
                  </button>
                  <AnimatePresence data-id="element-636">
                    {mobileHelpOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-637">
                        <div className="pt-4 pr-1 space-y-3" data-id="element-638">
                          <Link to="/help" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-639">
                            Material Quiz
                          </Link>
                          <Link to="/help?tab=calculator" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-640">
                            Calculator
                          </Link>
                          <Link to="/diy-help" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-641">
                            DIY Driveway
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
                <Link to="/delivery-zones" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-642">
                  Delivery Zones
                </Link>
                <div data-id="element-643">
                  <button onClick={() => setMobileAboutOpen(!mobileAboutOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-644">
                    Info
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${mobileAboutOpen ? 'rotate-180' : ''}`} data-id="element-645" />
                  </button>
                  <AnimatePresence data-id="element-646">
                    {mobileAboutOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-647">
                        <div className="pt-4 pr-1 space-y-3" data-id="element-648">
                          <Link to="/about" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-649">
                            About Us
                          </Link>
                          <Link to="/about#gallery" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-650">
                            Project Gallery
                          </Link>
                          <Link to="/subcontractors" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-651">
                            Contractors Zone
                          </Link>
                          <Link to="/#faq" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-652">
                            Da FAQs
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
                <Link to="/contact" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-653">
                  Contact
                </Link>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </nav>;
}